import React, { useState, useEffect } from 'react';
import ViralAutomationPanel from './ViralAutomationPanel';
import IntelligentPitchPanel from './IntelligentPitchPanel';
import './Dashboard.css';
import { getArtistId } from '../state/ArtistManager.js';
import { 
  getCatalogAnalytics, 
  getSpotifyAnalytics, 
  getGrowthAnalytics,
  getAccountingAnalytics 
} from '../services/AnalyticsService.js';
import API_BASE_URL from '../config/api.js';

const API_BASE = API_BASE_URL;

const Dashboard = ({ user, username, onSignOut }) => {
    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [spotifyConnected, setSpotifyConnected] = useState(false);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        setLoading(true);
        console.log('🔄 Loading real dashboard data...');

        const artist = getArtistId();

        try {
            // Fetch real data from multiple sources
            const [catalogData, spotifyData, growthData, accountingData] = await Promise.all([
                getCatalogAnalytics(artist).catch(() => null),
                getSpotifyAnalytics().catch(() => null),
                getGrowthAnalytics().catch(() => null),
                getAccountingAnalytics(artist).catch(() => null)
            ]);

            // Use real data if available, fall back to demo data
            const dashboardData = {
                catalog: {
                    artist,
                    totalTracks: catalogData?.metrics?.totalTracks || 9,
                    totalStreams: growthData?.totalStreams || 15420,
                    monthlyRevenue: accountingData?.totalRevenue || 127.50
                },
                analytics: {
                    portfolioValue: accountingData?.portfolioValue || '5,782.33',
                    monthlyRevenue: accountingData?.monthlyRevenue || '127.50',
                    growthRate: growthData?.monthlyGrowth ? `+${growthData.monthlyGrowth.toFixed(1)}%` : '+12.5%',
                    riskProfile: 'Medium-Low',
                    spotify: {
                        monthlyListeners: spotifyData?.monthlyListeners || 850,
                        followers: spotifyData?.followers || 1247,
                        recentStreams: { thisMonth: growthData?.monthlyStreams || 3200 }
                    },
                    topPerformers: catalogData?.topPerformers || [
                        { title: '999,999', revenue: '47.25', roi: '+18.5%' },
                        { title: 'OG Bashment', revenue: '32.80', roi: '+12.3%' },
                        { title: 'Monday', revenue: '28.15', roi: '+9.8%' }
                    ],
                    recommendations: [
                        {
                            priority: 'High',
                            action: 'Optimize "999,999" for sync licensing',
                            timeline: '2-4 weeks',
                            investment: '$50-100',
                            expectedROI: '15-25%'
                        },
                        {
                            priority: 'Medium',
                            action: 'Expand TikTok presence with OG Bashment',
                            timeline: '1-2 months',
                            investment: '$30-60',
                            expectedROI: '10-18%'
                        }
                    ],
                    weekly: growthData?.weeklyData || []
                }
            };

            setDashboardData(dashboardData);
            setSpotifyConnected(!!spotifyData);
            
        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSpotifyConnect = () => {
        setSpotifyConnected(true);
        console.log('🎧 Spotify connected (UI simulation)');
    };

    if (loading) {
        return (
            <div className="professional-container">
                <div className="professional-loading">
                    Loading your analytics dashboard...
                </div>
            </div>
        );
    }

    return (
        <div className="professional-container">
            {/* Dashboard Header */}
            <header className="professional-header">
                <h1>🎵 {getArtistId()} Analytics Hub</h1>
                <p>Your complete artist command center</p>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginTop: '1rem',
                    padding: '1rem',
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '8px'
                }}>
                    <span style={{ color: 'var(--text-secondary)' }}>Welcome back, {username}!</span>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                        {!spotifyConnected ? (
                            <button 
                                onClick={handleSpotifyConnect}
                                className="professional-button"
                                title="Connect your Spotify account for detailed analytics"
                            >
                                🎧 Connect Spotify
                            </button>
                        ) : (
                            <div style={{ color: 'var(--success-color)', fontWeight: '600' }}>
                                ✅ Spotify Connected
                            </div>
                        )}
                        <button onClick={onSignOut} className="professional-button professional-button-secondary">
                            Sign Out
                        </button>
                    </div>
                </div>
            </header>

            {/* Dashboard Overview Metrics */}
            <div className="professional-grid-4">
                <div className="professional-metric-card">
                    <div className="professional-metric-value">${dashboardData?.analytics?.portfolioValue}</div>
                    <div className="professional-metric-label">Portfolio Value</div>
                    <div className="professional-metric-change positive">{dashboardData?.analytics?.growthRate}</div>
                </div>
                <div className="professional-metric-card">
                    <div className="professional-metric-value">${dashboardData?.analytics?.monthlyRevenue}</div>
                    <div className="professional-metric-label">Monthly Revenue</div>
                </div>
                <div className="professional-metric-card">
                    <div className="professional-metric-value">{dashboardData?.catalog?.totalTracks}</div>
                    <div className="professional-metric-label">Total Tracks</div>
                </div>
                <div className="professional-metric-card">
                    <div className="professional-metric-value">{dashboardData?.analytics?.riskProfile}</div>
                    <div className="professional-metric-label">Risk Profile</div>
                </div>
            </div>

            {/* Dashboard Main Content */}
            <div className="professional-grid-2">
                {/* Portfolio Performance */}
                <div className="professional-card">
                    <div className="professional-card-header">
                        <h3 className="professional-card-title">🏆 Top Performing Tracks</h3>
                    </div>
                    <div className="professional-card-content">
                        <ul className="professional-list">
                            {dashboardData?.analytics?.topPerformers?.map((track, index) => (
                                <li key={index} className="professional-list-item">
                                    <div className="professional-list-item-content">
                                        <div className="professional-list-item-title">{track.title}</div>
                                        <div className="professional-list-item-meta">Revenue this month</div>
                                    </div>
                                    <div className="professional-list-item-value">
                                        ${track.revenue}
                                        <span className="professional-metric-change positive" style={{ fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                                            {track.roi}
                                        </span>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                {/* Spotify Analytics */}
                <div className="professional-card">
                    <div className="professional-card-header">
                        <h3 className="professional-card-title">🎧 Spotify Analytics</h3>
                    </div>
                    <div className="professional-card-content">
                        <div className="spotify-metrics-row" style={{ display: 'flex', gap: 'var(--grid-gap)', marginBottom: '1.5rem' }}>
                            <div className="professional-metric-card" style={{ flex: 1 }}>
                                <div className="professional-metric-value">
                                    {dashboardData?.analytics?.spotify?.monthlyListeners?.toLocaleString()}
                                </div>
                                <div className="professional-metric-label">Monthly Listeners</div>
                            </div>
                            <div className="professional-metric-card" style={{ flex: 1 }}>
                                <div className="professional-metric-value">
                                    {dashboardData?.analytics?.spotify?.followers?.toLocaleString()}
                                </div>
                                <div className="professional-metric-label">Followers</div>
                            </div>
                        </div>
                        <div className="professional-metric-card">
                            <div className="professional-metric-value">
                                {dashboardData?.analytics?.spotify?.recentStreams?.thisMonth?.toLocaleString()}
                            </div>
                            <div className="professional-metric-label">This Month's Streams</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Strategic Recommendations */}
            <div className="professional-card">
                <div className="professional-card-header">
                    <h3 className="professional-card-title">💡 Strategic Recommendations</h3>
                </div>
                <div className="professional-card-content">
                    <ul className="professional-list">
                        {dashboardData?.analytics?.recommendations?.map((rec, index) => (
                            <li key={index} className="professional-list-item">
                                <div className="professional-list-item-content">
                                    <div className="professional-list-item-title">{rec.action}</div>
                                    <div className="professional-list-item-meta">
                                        {rec.timeline} • Investment: {rec.investment} • ROI: {rec.expectedROI}
                                    </div>
                                </div>
                                <div className={`professional-list-item-value ${rec.priority === 'High' ? 'warning' : ''}`}>
                                    {rec.priority} Priority
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            {/* Quick Actions */}
            <div className="professional-grid-3">
                <div className="professional-card">
                    <div className="professional-card-header">
                        <h4 className="professional-card-title">📊 Analytics</h4>
                    </div>
                    <div className="professional-card-content">
                        <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                            Deep dive into your performance metrics
                        </p>
                        <a href="/insights" className="professional-button" style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}>
                            View Analytics
                        </a>
                    </div>
                </div>

                <div className="professional-card">
                    <div className="professional-card-header">
                        <h4 className="professional-card-title">🎵 Catalog</h4>
                    </div>
                    <div className="professional-card-content">
                        <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                            Manage your music catalog and track performance
                        </p>
                        <a href="/catalog" className="professional-button" style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}>
                            Manage Catalog
                        </a>
                    </div>
                </div>

                <div className="professional-card">
                    <div className="professional-card-header">
                        <h4 className="professional-card-title">🎯 Growth</h4>
                    </div>
                    <div className="professional-card-content">
                        <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                            AI-powered pitch system and automation tools
                        </p>
                        <a href="/growth/intelligent-pitch" className="professional-button" style={{ width: '100%', textAlign: 'center', textDecoration: 'none' }}>
                            Launch Pitches
                        </a>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
